import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import urls from '../util/urls';
import {
  compose,
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  withE2ERequestStubbing,
} from '../hooks/useService';
import { ServiceAuth } from '../hooks/useService/useService';

interface Gene {
  gene: string;
}

interface GeneProfileFilter {
  gene: string;
}

export interface GeneResponse {
  gene: string;
  id?: string | null;
}

export interface GeneSet {
  datasetId: string;
  description?: string;
  id: string;
  name?: string;
  genes?: Array<GeneResponse>;
}

export type DrugClassSearch = {
  drugClass?: string;
};

export type DrugClassSearchResponse = {
  items: {
    drugClass: string;
  }[];
};

export const stringifyFilters = (filters: { [key: string]: string }) => {
  let filterString = '';

  if (filters) {
    for (const filter in filters) {
      const filterValues = filters[filter].split(',');
      filterValues.forEach(
        (f: string) =>
          // Some values have specical characters that need to be encoded
          // Ex. therapy_name=AZD6738 + Cisplatin
          (filterString += `&${filter}=${encodeURIComponent(f).trim()}`),
      );
    }
  }

  return filterString;
};

export default class KnowledgeService {
  knowledgeAPI: (auth: ServiceAuth) => AxiosInstance;

  constructor(_options?: Partial<AxiosRequestConfig>) {
    this.knowledgeAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
  }

  getGenes =
    (auth: ServiceAuth) =>
    ({ gene }: Gene) => {
      return this.knowledgeAPI(auth).get(urls.api.knowledge.genes({ gene }));
    };

  getGeneProfiles =
    (auth: ServiceAuth) => (geneProfileFilter: GeneProfileFilter) => {
      return this.knowledgeAPI(auth).get(
        urls.api.knowledge.geneProfiles(geneProfileFilter),
      );
    };

  getGeneAggregates =
    (auth: ServiceAuth) =>
    ({ gene }: Gene) => {
      return this.knowledgeAPI(auth).get(
        urls.api.knowledge.geneAggregates({ gene }),
      );
    };

  getpfamDataSets = (auth: ServiceAuth) => (transcriptId: string) => {
    return this.knowledgeAPI(auth).get(
      urls.api.knowledge.pfamDataSets(transcriptId),
    );
  };

  getVariants = (auth: ServiceAuth) => (transcriptId: string) => {
    return this.knowledgeAPI(auth).get(
      urls.api.knowledge.variants(transcriptId),
    );
  };

  getGeneSets = (auth: ServiceAuth) => (projectId: string) => {
    return this.knowledgeAPI(auth).get(
      urls.api.knowledge.geneSets({
        datasetId: projectId,
      }),
    );
  };

  getDrugClasses =
    (auth: ServiceAuth) =>
    (params: DrugClassSearch = {}) => {
      return this.knowledgeAPI(auth).get<DrugClassSearchResponse>(
        urls.api.knowledge.drugClass(params),
      );
    };

  getBiochemistry =
    (auth: ServiceAuth) =>
    (props: {
      gene: Gene;
      filters?: any;
      nextPageToken?: string;
      pageSize?: number;
      suggest?: string;
    }) => {
      const { filters, ...restProps } = props;
      const filterString = stringifyFilters(filters);
      return this.knowledgeAPI(auth).get(
        urls.api.knowledge.biochemistry(filterString, {
          ...restProps,
        }),
      );
    };

  getEvidence =
    (auth: ServiceAuth) =>
    (props: {
      gene: Gene;
      filters?: any;
      nextPageToken?: string;
      pageSize?: number;
      suggest?: string;
    }) => {
      const { filters, ...restProps } = props;
      const filterString = stringifyFilters(filters);
      return this.knowledgeAPI(auth).get(
        urls.api.knowledge.evidence(filterString, {
          ...restProps,
        }),
      );
    };
}
